import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { FiPlus, FiEdit, FiTrash } from 'react-icons/fi';
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';
import { useForm } from 'react-hook-form';
import ConditionalButton from '../components/common/ConditionalButton';
import ConditionalActions from '../components/common/ConditionalActions';

const ProfessionPage = () => {
  //const { user } = useAuth();
  const [professions, setProfessions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingProfession, setEditingProfession] = useState(null);
  const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm();

  const fetchProfessions = async () => {
    const res = await api.get('/professions');
    setProfessions(res.data.data || []);
  };

  useEffect(() => {
    fetchProfessions();
  }, []);

  const onSubmit = async (data) => {
    if (editingProfession) {
      await api.put(`/professions/${editingProfession.id}`, data);
    } else {
      await api.post('/professions', data);
    }
    setShowModal(false);
    setEditingProfession(null);
    reset();
    fetchProfessions();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Professions</h1>
        <ConditionalButton
          entity="professions"
          permission="create"
          className="flex items-center space-x-2 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700"
          onClick={() => setShowModal(true)}
        >
          <FiPlus />
          <span>Nouvelle profession</span>
        </ConditionalButton>
      </div>
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Profession</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Description</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {professions.length === 0 ? (
              <tr>
                <td colSpan="3" className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">Aucune profession</td>
              </tr>
            ) : (
              professions.map((prof) => (
                <tr key={prof.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/80 transition-colors duration-150">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-200">{prof.profession}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">{prof.description}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <ConditionalActions
                      entity="professions"
                      onEdit={() => {
                        setEditingProfession(prof);
                        setShowModal(true);
                        setValue('profession', prof.profession);
                        setValue('description', prof.description);
                      }}
                      onDelete={async () => {
                        const result = await Swal.fire({
                          title: 'Supprimer cette profession ?',
                          text: 'Cette action est irréversible.',
                          icon: 'warning',
                          showCancelButton: true,
                          confirmButtonColor: '#d33',
                          cancelButtonColor: '#3085d6',
                          confirmButtonText: 'Oui, supprimer',
                          cancelButtonText: 'Annuler',
                        });
                        if (result.isConfirmed) {
                          await api.delete(`/professions/${prof.id}`);
                          fetchProfessions();
                          Swal.fire('Supprimé !', 'La profession a été supprimée.', 'success');
                        }
                      }}
                      editIcon={FiEdit}
                      deleteIcon={FiTrash}
                      className="flex space-x-2 justify-end"
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-30 dark:bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 w-full max-w-md">
            <h2 className="text-xl font-semibold mb-4 dark:text-gray-100">{editingProfession ? 'Modifier' : 'Ajouter une'} profession</h2>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1 dark:text-gray-200">Profession *</label>
                <input {...register('profession', { required: true })} className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500" />
                {errors.profession && <span className="text-red-500 dark:text-red-400 text-xs mt-1">Champ obligatoire</span>}
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1 dark:text-gray-200">Description</label>
                <input {...register('description')} className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500" />
              </div>
              <div className="flex justify-end space-x-2">
                <button type="button" className="px-4 py-2 bg-gray-200 dark:bg-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600" onClick={() => { setShowModal(false); setEditingProfession(null); reset(); }}>Annuler</button>
                <ConditionalButton
                  entity="professions"
                  permission={editingProfession ? "update" : "create"}
                  type="submit"
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  Enregistrer
                </ConditionalButton>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfessionPage;
